import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { getSessionId } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to extract YouTube video ID
const getYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// Helper to extract Vimeo video ID
const getVimeoId = (url) => {
  if (!url) return null;
  const regExp = /vimeo\.com\/(?:video\/)?(\d+)/;
  const match = url.match(regExp);
  return match ? match[1] : null;
};

export default function AboutPage() {
  const { language } = useLanguage();
  const [videoData, setVideoData] = useState({ 
    video_url: null, 
    video_type: 'youtube',
    video_title_fr: 'Découvrez Belleville Machine',
    video_title_en: 'Discover Belleville Machine'
  });

  useEffect(() => {
    // Log visitor
    axios.post(`${API}/visitor/log`, {
      page: 'about',
      session_id: getSessionId(),
      user_agent: navigator.userAgent,
      referrer: document.referrer
    }).catch(() => {});

    // Fetch video content
    axios.get(`${API}/about-content`)
      .then(res => setVideoData(res.data))
      .catch(() => {});
  }, []);

  const renderVideo = () => {
    if (!videoData.video_url) return null;

    const { video_url, video_type } = videoData;

    if (video_type === 'youtube') {
      const videoId = getYouTubeId(video_url);
      if (!videoId) return null;
      return (
        <div className="aspect-video w-full">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title="Belleville Machine Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      );
    }

    if (video_type === 'vimeo') {
      const videoId = getVimeoId(video_url);
      if (!videoId) return null;
      return (
        <div className="aspect-video w-full">
          <iframe
            src={`https://player.vimeo.com/video/${videoId}`}
            title="Belleville Machine Video"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      );
    }

    // Direct video URL (MP4, etc.)
    return (
      <div className="aspect-video w-full">
        <video 
          src={video_url} 
          controls 
          className="w-full h-full object-cover"
          poster=""
        >
          Votre navigateur ne supporte pas la lecture vidéo.
        </video>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Hero Image */}
      <div className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <img 
          src="https://customer-assets.emergentagent.com/job_belleville-bikes/artifacts/2ypb3sdh_Capture%20d%E2%80%99e%CC%81cran%202026-03-03%20a%CC%80%2012.44.21.png"
          alt="Max - Belleville Machine"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-impact text-4xl md:text-6xl uppercase tracking-tight text-white"
          >
            Belleville Machine
          </motion.h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <p className="font-impact text-2xl md:text-3xl leading-relaxed mb-8">
            {language === 'fr' 
              ? "Je m'appelle Max."
              : "My name is Max."}
          </p>
          
          <p className="font-body text-lg leading-relaxed text-muted-foreground mb-6">
            {language === 'fr'
              ? "Je suis artisan cadreur et fondateur de Belleville Machine, atelier de fabrication indépendant né il y a vingt ans."
              : "I am a frame builder and founder of Belleville Machine, an independent workshop born twenty years ago."}
          </p>

          <p className="font-body text-lg leading-relaxed text-muted-foreground mb-6">
            {language === 'fr'
              ? "Pendant dix ans, j'ai mené une double vie : à mi-temps coursier à vélo, à mi-temps cadreur. Dix années à rouler chaque jour, par tous les temps. À charger, livrer, freiner, relancer. À comprendre profondément ce qu'un vélo doit être : fiable, solide et bien monté. Une machine faite pour rouler."
              : "For ten years, I led a double life: part-time bike courier, part-time frame builder. Ten years of riding every day, in all weather. Loading, delivering, braking, accelerating. Deeply understanding what a bike must be: reliable, solid and well-built. A machine made to ride."}
          </p>

          <p className="font-impact text-lg text-[#0047AB] my-8">
            {language === 'fr'
              ? "C'est là que s'est forgée ma vision."
              : "That's where my vision was forged."}
          </p>

          <p className="font-body text-lg leading-relaxed text-muted-foreground mb-6">
            {language === 'fr'
              ? "Pendant dix ans, j'ai conçu et fabriqué des vélos sur mesure — route, pignon fixe, puis gravel — en travaillant chaque géométrie, chaque détail, chaque soudure. L'objectif était simple : créer des cadres uniques, pensés pour rouler."
              : "For ten years, I designed and built custom bikes — road, fixed gear, then gravel — working on every geometry, every detail, every weld. The goal was simple: create unique frames, designed to ride."}
          </p>

          <div className="my-12 p-8 bg-secondary">
            <h2 className="font-impact text-2xl uppercase mb-4">
              {language === 'fr'
                ? "Aujourd'hui, Belleville Machine se consacre exclusivement au vélo cargo."
                : "Today, Belleville Machine is exclusively dedicated to cargo bikes."}
            </h2>
            <p className="font-body text-lg leading-relaxed text-muted-foreground">
              {language === 'fr'
                ? "Parce qu'un cargo bien conçu n'est pas seulement pratique : il doit rester maniable. Il doit donner envie de rouler, chargé ou non. Il doit garder ce lien direct entre le cadre, la route et le pilote."
                : "Because a well-designed cargo bike is not just practical: it must remain maneuverable. It must make you want to ride, loaded or not. It must maintain that direct connection between the frame, the road and the rider."}
            </p>
          </div>

          <p className="font-body text-lg leading-relaxed text-muted-foreground mb-6">
            {language === 'fr'
              ? "Chaque vélo est imaginé et fabriqué dans mon atelier, dans le sud de la forêt de Fontainebleau, à Nanteau-sur-Essonne. Un travail artisanal où l'expérience du terrain guide chaque choix technique."
              : "Each bike is designed and built in my workshop, south of the Fontainebleau forest, in Nanteau-sur-Essonne. Craftsmanship where field experience guides every technical choice."}
          </p>

          <div className="my-12 p-8 border-2 border-[#0047AB]">
            <p className="font-impact text-xl mb-4">
              {language === 'fr'
                ? "En 2025, j'ai lancé le FOLGO — The FOLdable carGO."
                : "In 2025, I launched the FOLGO — The FOLdable carGO."}
            </p>
            <p className="font-body text-lg leading-relaxed text-muted-foreground">
              {language === 'fr'
                ? "Un vélo cargo pliant pensé sans compromis : compact une fois replié, rigide et fluide en mouvement. Le seul vélo cargo accepté dans le TGV et TER."
                : "A folding cargo bike designed without compromise: compact when folded, rigid and fluid in motion. The only cargo bike accepted on TGV and TER trains."}
            </p>
          </div>

          {/* Video Section */}
          {videoData.video_url && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="my-12"
              data-testid="about-video-section"
            >
              <h3 className="font-impact text-xl uppercase mb-6 text-center">
                {language === 'fr' ? videoData.video_title_fr : videoData.video_title_en}
              </h3>
              {renderVideo()}
            </motion.div>
          )}

          <p className="font-body text-lg leading-relaxed text-muted-foreground mb-8">
            {language === 'fr'
              ? "Chez Belleville Machine, il y a des cadres façonnés avec exigence, pensés pour encaisser et durer."
              : "At Belleville Machine, there are frames crafted with rigor, designed to take a beating and last."}
          </p>

          <div className="border-t-4 border-black pt-8 mt-12">
            <p className="font-impact text-2xl md:text-3xl uppercase">
              {language === 'fr'
                ? "Belleville Machine — des vélos faits pour rouler."
                : "Belleville Machine — bikes made to ride."}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
